/**
 * 注册家长账号。
 *
 * 校验全部复用 shared 的 zod schema（parentRegisterSchema / usernameSchema / emailSchema）
 * 与 checkPasswordStrength，前端不重写一遍规则，避免"前后端两套说法"。
 * 身份下拉只给 爸爸 / 妈妈（PARENT_RELATION_PRESETS，已与用户确认）。
 */
import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  PARENT_MIN_PASSWORD_LENGTH,
  PARENT_RELATION_PRESETS,
  checkPasswordStrength,
  emailSchema,
  parentRegisterSchema,
  usernameSchema,
} from '@fht/shared';
import * as authApi from '../../lib/api/auth';
import { toErrorMessage } from '../../lib/api/error';
import { Button } from '../../components/ui/Button';
import { Input, Select } from '../../components/ui/Input';
import { PasswordStrengthMeter } from '../../components/ui/PasswordStrengthMeter';
import { BuildStamp } from '../../components/layout/BuildStamp';

interface FieldErrors {
  nickname?: string;
  username?: string;
  email?: string;
  password?: string;
  relationLabel?: string;
  familyName?: string;
}

function firstIssue(error: unknown): { path?: string; message: string } | null {
  const issues = (error as { issues?: Array<{ path: Array<string | number>; message: string }> })
    .issues;
  if (!Array.isArray(issues) || issues.length === 0) return null;
  const issue = issues[0];
  if (!issue) return null;
  return { path: String(issue.path[0] ?? ''), message: issue.message };
}

export function RegisterPage() {
  const navigate = useNavigate();

  const [nickname, setNickname] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [relationLabel, setRelationLabel] = useState<string>(PARENT_RELATION_PRESETS[0]);
  const [familyName, setFamilyName] = useState('');

  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const passwordCheck = password
    ? checkPasswordStrength(password, {
        minLength: PARENT_MIN_PASSWORD_LENGTH,
        identities: [username, email, nickname],
      })
    : null;

  /** 单个字段即时校验（失焦时触发），只报该字段的问题。 */
  const validateField = (field: keyof FieldErrors, value: string) => {
    let message: string | undefined;
    if (field === 'username') {
      const parsed = usernameSchema.safeParse(value);
      if (!parsed.success) message = parsed.error.issues[0]?.message;
    }
    if (field === 'email') {
      const parsed = emailSchema.safeParse(value);
      if (!parsed.success) message = parsed.error.issues[0]?.message;
    }
    if (field === 'nickname' && value.trim().length === 0) {
      message = '请填写昵称';
    }
    if (field === 'nickname' && value.trim().length > 32) {
      message = '昵称最多 32 位';
    }
    setErrors((prev) => ({ ...prev, [field]: message }));
  };

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setFormError(null);

    // 用 shared 的 schema 整体校验一次，错误映射到具体字段
    const parsed = parentRegisterSchema.safeParse({
      nickname,
      username,
      email,
      password,
      relationLabel,
      ...(familyName.trim() ? { familyName: familyName.trim() } : {}),
    });

    if (!parsed.success) {
      const next: FieldErrors = {};
      for (const issue of parsed.error.issues) {
        const key = String(issue.path[0] ?? '') as keyof FieldErrors;
        if (key && !next[key]) next[key] = issue.message;
      }
      setErrors(next);
      setFormError('请检查标红的内容');
      return;
    }

    // 密码强度用策略函数判定，而不是靠 zod 的简单长度校验
    if (passwordCheck && !passwordCheck.ok) {
      setErrors((prev) => ({ ...prev, password: passwordCheck.reason }));
      setFormError(passwordCheck.reason ?? '密码强度不足');
      return;
    }

    setSubmitting(true);
    try {
      await authApi.registerParent(parsed.data);
      // 注册成功后不自动登录（后端裁定），去验证邮箱，再回登录页
      navigate(`/register/verify?email=${encodeURIComponent(parsed.data.email)}`, {
        replace: true,
      });
    } catch (err) {
      const issue = firstIssue((err as { details?: unknown }).details);
      setFormError(issue?.message ?? toErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4 py-8">
      <div className="w-full max-w-lg">
        <div className="mb-6 text-center">
          <h1 className="text-2xl font-bold text-slate-900">注册家长账号</h1>
          <p className="mt-1 text-sm text-slate-500">
            注册后创建孩子账号，即可开始记录学习打卡
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          noValidate
          className="space-y-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6"
        >
          <Input
            label="昵称"
            name="nickname"
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            onBlur={() => validateField('nickname', nickname)}
            required
            autoComplete="nickname"
            {...(errors.nickname ? { error: errors.nickname } : {})}
          />

          <Input
            label="用户名"
            name="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            onBlur={() => validateField('username', username)}
            required
            autoComplete="username"
            hint="3~32 位，字母开头，可用字母、数字、下划线、连字符"
            {...(errors.username ? { error: errors.username } : {})}
          />

          <Input
            label="邮箱"
            name="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => validateField('email', email)}
            required
            autoComplete="email"
            hint="用于邮箱验证和找回密码"
            {...(errors.email ? { error: errors.email } : {})}
          />

          <div className="space-y-2">
            <Input
              label="密码"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="new-password"
              hint={`至少 ${PARENT_MIN_PASSWORD_LENGTH} 位，不要使用常见弱口令或包含用户名/邮箱`}
              {...(errors.password ? { error: errors.password } : {})}
              suffix={
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="rounded-lg px-2 py-1.5 text-xs font-medium text-brand-600 hover:bg-brand-50"
                  aria-label={showPassword ? '隐藏密码' : '显示密码'}
                >
                  {showPassword ? '隐藏' : '显示'}
                </button>
              }
            />
            <PasswordStrengthMeter password={password} />
            {passwordCheck && !passwordCheck.ok && (
              <p className="text-xs text-amber-600">{passwordCheck.reason}</p>
            )}
          </div>

          <Select
            label="身份"
            name="relationLabel"
            value={relationLabel}
            onChange={(e) => setRelationLabel(e.target.value)}
            {...(errors.relationLabel ? { error: errors.relationLabel } : {})}
          >
            {PARENT_RELATION_PRESETS.map((preset) => (
              <option key={preset} value={preset}>
                {preset}
              </option>
            ))}
          </Select>

          <Input
            label="家庭名称（可选）"
            name="familyName"
            value={familyName}
            onChange={(e) => setFamilyName(e.target.value)}
            hint="例如「我们家的学习角」，会显示在家庭设置里"
            {...(errors.familyName ? { error: errors.familyName } : {})}
          />

          {formError && (
            <div role="alert" className="rounded-xl bg-rose-50 px-3 py-2.5 text-sm text-rose-700">
              {formError}
            </div>
          )}

          <Button type="submit" fullWidth size="lg" loading={submitting}>
            注册并发送验证码
          </Button>

          <p className="text-center text-sm text-slate-500">
            已有账号？
            <Link to="/login" className="font-medium text-brand-600 hover:underline">
              去登录
            </Link>
          </p>
        </form>

        {/*
          「构建于 …」版本标记（2026-10-16 补）。

          与家庭端其它未登录页同一个位置关系：居中卡片（这里是表单本身）**下方的页脚处居中**。
          复用全站唯一的 `BuildStamp`（见 lib/buildStamp.ts），不另写实现、不复制样式。
        */}
        <BuildStamp className="mt-4 text-center" />
      </div>
    </div>
  );
}
